import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Gallery", path: "/gallery" },
    { name: "Tour", path: "/tour" },
    { name: "Travel", path: "/travel" },
    { name: "Spoil Me", path: "/gifts" },
    { name: "Q&A", path: "/faq" },
    { name: "Blog", path: "/blog" },
    { name: "Links", path: "/links" },
    { name: "Contact", path: "/contact" },
  ];

  const P411 = { href: "https://preferred411.com/admirer/register?ref=350697", img: "https://preferred411.com/a/preferredSeal-g.png" };

  const secondaryLinks = [
    { name: "Booking Etiquette", path: "/booking-etiquette" },
    { name: "Inquiry", path: "/booking" },
  ];

  return (
    <>
      <nav 
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-ivory/95 backdrop-blur-md shadow-soft" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between gap-6 h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
              <span className="font-accent text-2xl text-champagne-gold">Kalani Luana</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-7 min-w-0">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link whitespace-nowrap ${location.pathname === link.path ? "text-champagne-gold" : ""}`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-4 shrink-0">
              <Link to="/booking-etiquette">
                <span className="nav-link whitespace-nowrap" data-testid="nav-etiquette">Etiquette</span>
              </Link>
              <Link to="/booking">
                <Button className="btn-primary text-xs py-2 px-6" data-testid="nav-inquiry-btn">
                  Inquiry
                </Button>
              </Link>
              <a href={P411.href} target="_blank" rel="noopener noreferrer" className="ml-2 transition-transform duration-300 hover:scale-110" data-testid="nav-p411-badge" title="Verified on Preferred411.com">
                <img src={P411.img} alt="Preferred411.com" width="109" height="119" className="h-14 w-auto drop-shadow-sm" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-charcoal" />
              ) : (
                <Menu className="w-6 h-6 text-charcoal" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-12 right-6"
          >
            <X className="w-8 h-8 text-charcoal" />
          </button>

          <div className="flex flex-col items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`mobile-menu-link ${location.pathname === link.path ? "text-champagne-gold" : ""}`}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            
            <div className="section-divider" />
            
            {secondaryLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="mobile-menu-link text-lg"
              >
                {link.name}
              </Link>
            ))}

            <Link to="/booking" className="mt-4">
              <Button className="btn-primary">
                Submit Inquiry
              </Button>
            </Link>

            <a href={P411.href} target="_blank" rel="noopener noreferrer" className="mt-2" data-testid="mobile-nav-p411-badge">
              <img src={P411.img} alt="Preferred411.com" width="109" height="119" className="h-16 w-auto" />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
